/**
 * BackfillBriefOverlay — modal for linking an ingested creative back to an
 * existing brief. Brief typeahead is filtered by current clientUid; angle
 * picker activates after a brief is chosen.
 */

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { adCatalogApi, generationApi } from '@/services/api';
import type { GenerationBrief } from '@/services/api';

interface Props {
  clientUid: string;
  creativeId: string;
  isOpen: boolean;
  onClose: () => void;
  onLinked: (briefId: string, briefVersion: number, angleId?: string) => void;
}

interface BriefOption {
  brief_id: string;
  title: string;
  version: number;
  status: string;
}

export function BackfillBriefOverlay({
  clientUid,
  creativeId,
  isOpen,
  onClose,
  onLinked,
}: Props) {
  const [query, setQuery] = useState('');
  const [briefs, setBriefs] = useState<BriefOption[]>([]);
  const [selectedBrief, setSelectedBrief] = useState<BriefOption | null>(null);
  const [briefDetail, setBriefDetail] = useState<GenerationBrief | null>(null);
  const [selectedAngleId, setSelectedAngleId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state on each open.
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSelectedBrief(null);
      setBriefDetail(null);
      setSelectedAngleId(null);
      setError(null);
    }
  }, [isOpen]);

  // Load briefs for this client when the overlay opens.
  useEffect(() => {
    if (!isOpen || !clientUid) return;
    let cancelled = false;
    generationApi
      .listBriefs({ client_uid: clientUid, limit: 50 })
      .then((resp) => {
        if (cancelled) return;
        setBriefs(
          resp.briefs.map((b) => ({
            brief_id: b.brief_id,
            title: b.name ?? '(untitled)',
            version: b.version ?? 1,
            status: b.status ?? 'unknown',
          })),
        );
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load briefs');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, clientUid]);

  // Load full brief detail (for angle picker) when one is chosen.
  useEffect(() => {
    if (!selectedBrief) {
      setBriefDetail(null);
      setSelectedAngleId(null);
      return;
    }
    let cancelled = false;
    generationApi
      .getBrief(selectedBrief.brief_id)
      .then((b) => {
        if (!cancelled) setBriefDetail(b);
      })
      .catch(() => {
        if (!cancelled) setBriefDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedBrief]);

  const filteredBriefs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return briefs;
    return briefs.filter(
      (b) => b.title.toLowerCase().includes(q) || b.brief_id.toLowerCase().includes(q),
    );
  }, [briefs, query]);

  const handleLink = async () => {
    if (!selectedBrief) return;
    setSubmitting(true);
    setError(null);
    try {
      await adCatalogApi.linkBrief(clientUid, creativeId, {
        brief_id: selectedBrief.brief_id,
        brief_version: selectedBrief.version,
        ...(selectedAngleId ? { angle_id: selectedAngleId } : {}),
      });
      onLinked(selectedBrief.brief_id, selectedBrief.version, selectedAngleId ?? undefined);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to link brief');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const angles = (briefDetail?.selected_angles ?? []) as Array<{
    angle_id: string;
    hook?: string;
    message?: string;
  }>;

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
        aria-label="Close backfill overlay"
      />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-6">
        <div
          className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md p-5 space-y-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-navy-900">Link to Brief</h3>
            <button type="button" onClick={onClose}>
              <X size={16} className="text-slate-400" />
            </button>
          </div>

          <p className="text-xs text-slate-600">
            Linking this ad to a brief enables performance tracking at the brief level and unlocks
            the PDO strategy vs. execution comparison.
          </p>

          <div className="space-y-1.5">
            <p className="text-[10px] font-mono uppercase text-slate-400">Search brief</p>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter…"
              className="w-full text-xs border border-slate-200 rounded-lg p-2 focus:outline-none focus:border-teal-400"
            />
            <ul className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white">
              {filteredBriefs.length === 0 && (
                <li className="text-[11px] text-slate-400 italic px-2 py-1.5">
                  {briefs.length === 0
                    ? 'No briefs found for this client.'
                    : 'No briefs match the search.'}
                </li>
              )}
              {filteredBriefs.map((b) => (
                <li
                  key={b.brief_id}
                  className={`flex items-center justify-between px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50 ${
                    selectedBrief?.brief_id === b.brief_id ? 'bg-teal-50' : ''
                  }`}
                  onClick={() => setSelectedBrief(b)}
                >
                  <span className="truncate text-navy-900">{b.title}</span>
                  <span className="text-[10px] font-mono text-slate-400 ml-2 flex-shrink-0">
                    v{b.version} · {b.status}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {selectedBrief && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-mono uppercase text-slate-400">
                Pick an angle (optional)
              </p>
              {angles.length === 0 ? (
                <p className="text-[11px] text-slate-400 italic">
                  This brief has no angles yet.
                </p>
              ) : (
                <ul className="max-h-32 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white">
                  {angles.map((a) => (
                    <li
                      key={a.angle_id}
                      className={`px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50 ${
                        selectedAngleId === a.angle_id ? 'bg-teal-50' : ''
                      }`}
                      onClick={() => setSelectedAngleId(a.angle_id)}
                    >
                      <p className="font-medium text-navy-900 truncate">
                        {a.hook ?? a.message ?? a.angle_id}
                      </p>
                      <p className="text-[10px] font-mono text-slate-400">{a.angle_id}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {error && (
            <p className="text-[11px] text-rose-600 bg-rose-50 border border-rose-200 rounded p-2">
              {error}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 text-xs text-slate-500 border border-slate-200 rounded-xl py-2 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleLink}
              disabled={!selectedBrief || submitting}
              className="flex-1 bg-teal-500 hover:bg-teal-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-xl py-2 transition-colors"
            >
              {submitting ? 'Linking…' : 'Link Brief →'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
